import React, { useEffect, useState } from 'react';

import { Application } from 'components/JobBoardDetails'
import { CandidateDetails } from "components/CandidateView";
import Api from 'api';
import AvatarWithInitials from '../AvatarWithInitials/AvatarWithInitials';
import { formatFirstLicensedLabel } from 'utils/educationCredentials';
import { getCanonicalMembershipBadge } from 'utils/membership';

import {
    ItemContainer,
    Badge,
    TopSection,
    Image,
    AvatarWrapper,
    Titles,
    CandidateName,
    CandidateExperience,
    StyledProImage,
    StyledEliteImage,
    StyledPersonOutlineIcon,
    StyledThumbUpAltOutlinedIcon,
    StyledThumbDownAltOutlinedIcon,
    NewCandidateBadge,
    ShortlistedCandidateBadge,
    DeclinedCandidateBadge,
    DetailViewInfo,
    DetailViewInfoItem,
    StarIconInactive,
    StarIconActive,
    NoRatingText,
    NoRatingTextLarge,
    RatingCountText,
    ViewCandidateButton,
    VerticalInfoStack,
    InfoLine
} from "./ApplicationsList.styles";
import Rating from "@mui/material/Rating";
import {HYGIENIST_TYPES} from "../../utils/types";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// Extend Application type to include rating fields that might come from API
interface ApplicationWithRatingFields extends Application {
    ratings_num?: number;
    ratingsNum?: number;
    reviewsCount?: number;
    completedJobs?: number;
    ratingCount?: number;
}

interface ApplicationsListItemProps {
    application: Application
    onApplicationClick?: (application: Application) => void
    isNew?: boolean
    isShortlisted?: boolean
    candidateDetails?: CandidateDetails
    isHidden?: boolean
    isDeclined?: boolean
}

const ApplicationsListItem = ({application, onApplicationClick, isNew, isShortlisted, candidateDetails, isHidden, isDeclined}: ApplicationsListItemProps) => {
    const [hygienistStats, setHygienistStats] = useState<{ ratings_num?: number; ratingsNum?: number; reviewsCount?: number; completedJobs?: number; starScore?: number }>({});
    
    // Use properly typed interface instead of 'as any'
    const applicationWithExtras = application as ApplicationWithRatingFields;
    
    useEffect(() => {
        const fetchHygienistStats = async () => {
            try {
                const stats = await Api.getHygienistStatsById({ hygienistId: application.hygienistId, applicationId: application.applicationId });
                setHygienistStats(stats);
            } catch (error) {
                console.error("Error fetching hygienist stats:", error);
            }
        };

        fetchHygienistStats();
    }, [application.hygienistId, application.applicationId]);

    const isHygienistOrAssistant = application.type === HYGIENIST_TYPES.HYGIENIST
        || application.type === HYGIENIST_TYPES.ASSISTANT;
    const membershipBadge = getCanonicalMembershipBadge(application.membershipStatusCode);

    // Use firstName and lastName directly from application if available
    // Fallback to parsing the name field for backward compatibility
    let firstName = application.firstName || '';
    let lastName = application.lastName || '';
    
    // If firstName/lastName not available, parse from name field as fallback
    if (!firstName && !lastName && application.name) {
        // Remove professional designations (RDH, RDA, etc.) before parsing
        const cleanName = application.name.replace(/,?\s*(RDH|RDA|CDA|DA|DDS|DMD|MBA|BSc|BA|BS|MS|MA|PhD|Dr\.?)\s*$/i, '').trim();
        const nameParts = cleanName.split(/\s+/);
        firstName = nameParts[0] || '';
        lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
    }

    return (
        <ItemContainer $isNew={!!isNew} className="no-fastclick" onClick={() => onApplicationClick && onApplicationClick(application)}>
            <TopSection>
                <AvatarWrapper>
                    <AvatarWithInitials
                        photoUrl={application.resumeViewed ? application.photoUrl : null} // Show photo only if contact info is unmasked
                        firstName={firstName}
                        lastName={lastName}
                        initialsOverride={application.resumeViewed ? undefined : (application.name || undefined)}
                    />
                </AvatarWrapper>
                <Titles $isDetailView={!!candidateDetails}>
                    {membershipBadge ? (
                        <Badge>
                            {membershipBadge === 'pro' && <StyledProImage alt="Pro membership" />}
                            {membershipBadge === 'elite' && <StyledEliteImage alt="Elite membership" />}
                        </Badge>
                    ) : null}
                    <CandidateName $isDetailView={!!candidateDetails} $isHidden={isHidden || false}>{application.name}</CandidateName>
                    {!candidateDetails && (
                        <>
                            <CandidateExperience>
                                Graduated: {application.graduationYear} from: {application.school}
                            </CandidateExperience>
                            {formatFirstLicensedLabel(application.firstLicensedYear, application.firstLicensedMonth) ? (
                                <CandidateExperience>
                                    {formatFirstLicensedLabel(application.firstLicensedYear, application.firstLicensedMonth)}
                                </CandidateExperience>
                            ) : null}
                            {isHygienistOrAssistant && (
                                <DetailViewInfoItem>
                                    {(() => {
                                        // Check multiple possible field names for rating count
                                        // First check the hygienistStats from API
                                        let ratingCount = hygienistStats.ratings_num || 
                                                        hygienistStats.ratingsNum || 
                                                        hygienistStats.reviewsCount || 
                                                        hygienistStats.completedJobs || 
                                                        0;
                                        
                                        // Also check if the application object itself has rating count data
                                        if (!ratingCount && applicationWithExtras) {
                                            ratingCount = applicationWithExtras.ratings_num || 
                                                        applicationWithExtras.ratingsNum || 
                                                        applicationWithExtras.reviewsCount || 
                                                        applicationWithExtras.completedJobs ||
                                                        applicationWithExtras.ratingCount ||
                                                        0;
                                        }
                                        
                                        // Use fresh star score from API if available, otherwise fall back to application.rating
                                        const starScore = hygienistStats.starScore !== undefined ? 
                                                         Number(hygienistStats.starScore) : 
                                                         (application.rating !== undefined ? application.rating : 0);
                                        
                                        if (ratingCount > 0 && starScore > 0) {
                                            return (
                                                <>
                                                    <Rating
                                                        name="candidateRating"
                                                        value={starScore}
                                                        readOnly
                                                        precision={0.01}
                                                        emptyIcon={<StarIconInactive />}
                                                        icon={<StarIconActive />}
                                                    />
                                                    <div>{ratingCount} {ratingCount === 1 ? 'Rating' : 'Ratings'}</div>
                                                </>
                                            );
                                        } else {
                                            return <NoRatingText>This candidate has not yet been rated on TempStars.</NoRatingText>;
                                        }
                                    })()}
                                </DetailViewInfoItem>
                            )}
                        </>
                    )}
                    {candidateDetails && (
                        <VerticalInfoStack> 
                            <InfoLine>
                                Graduation Year: {application.graduationYear} {application.school ? `from ${application.school}` : ''}
                            </InfoLine>
                            {formatFirstLicensedLabel(application.firstLicensedYear, application.firstLicensedMonth) ? (
                                <InfoLine>
                                    {formatFirstLicensedLabel(application.firstLicensedYear, application.firstLicensedMonth)}
                                </InfoLine>
                            ) : null}
                            {(application.city || application.province) && (
                                <InfoLine>
                                    {application.city && application.province ? `${application.city}, ${application.province}` : (application.city || application.province)}
                                </InfoLine>
                            )}
                            {(candidateDetails.reviewsCount !== undefined && candidateDetails.reviewsCount > 0) ? (
                                <InfoLine>
                                    <Rating
                                        name="candidateRating"
                                        value={hygienistStats.starScore !== undefined ? Number(hygienistStats.starScore) : (application.rating || 0)} 
                                        readOnly
                                        precision={0.01}
                                        emptyIcon={<StarIconInactive />}
                                        icon={<StarIconActive />}
                                        size="small"
                                    />
                                    <RatingCountText style={{ marginLeft: '8px', display: 'inline' }}>{candidateDetails.reviewsCount} {candidateDetails.reviewsCount === 1 ? 'Rating' : 'Ratings'}</RatingCountText>
                                </InfoLine>
                            ) : (
                                <NoRatingTextLarge>This candidate has not yet been rated on TempStars.</NoRatingTextLarge>
                            )}
                        </VerticalInfoStack>
                    )}
                </Titles>
                {isNew ? (
                        <NewCandidateBadge $isDetailView={!!candidateDetails}>
                            <StyledPersonOutlineIcon />
                            <span>New</span>
                        </NewCandidateBadge>
                    ) : null}
                    {isShortlisted ? (
                        <ShortlistedCandidateBadge $isDetailView={!!candidateDetails}>
                            <StyledThumbUpAltOutlinedIcon />
                            <span>Shortlisted</span>
                        </ShortlistedCandidateBadge>
                    ) : null}
                    {isDeclined ? (
                        <DeclinedCandidateBadge $isDetailView={!!candidateDetails}>
                            <StyledThumbDownAltOutlinedIcon />
                            <span>Declined</span>
                        </DeclinedCandidateBadge>
                    ) : null}
            </TopSection>
            {!candidateDetails && (
                <ViewCandidateButton 
                    variant="text" 
                    size="small"
                    onClick={(e) => {
                        // T2-6731: button sits inside ItemContainer which also
                        // calls onApplicationClick — stop bubble to avoid a
                        // double history.push (Back needing two taps).
                        e.stopPropagation();
                        onApplicationClick && onApplicationClick(application);
                    }}
                    endIcon={<ChevronRightIcon />}
                >
                    View Candidate
                </ViewCandidateButton>
            )}
        </ItemContainer>
    )
}

export default ApplicationsListItem;
